import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { homePageGuard } from '@core/guards';
import { ChunkLoadErrorHandlerModule } from '@features/chunk-load-error-handler';
import { accessPageGuard } from '@features/security';
import { HomeComponent } from '../../layout/components';
import { HomeOrganizationComponent } from './components/home-organization/home-organization.component';
import { PageOrganizationComponent } from './components/page-organization/page-organization.component';
import { InternalUserOrganizationResolver } from './resolver';

const routes: Routes = [
  {
    path: '',
    component: HomeOrganizationComponent,
    pathMatch: 'full',
  },
  {
    path: ':organizationId',
    component: PageOrganizationComponent,
    resolve: { organization: InternalUserOrganizationResolver },
    children: [
      {
        path: 'home',
        canActivate: [homePageGuard],
        component: HomeComponent,
        pathMatch: 'full',
      },
      {
        path: 'projects',
        canActivate: [accessPageGuard],
        loadChildren: () =>
          import('@features/projects/projects.module')
            .then((mod) => mod.ProjectsModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        data: {
          permissions: ['view_projects'],
        },
      },
      {
        path: 'data-sources',
        canActivate: [accessPageGuard],
        loadChildren: () =>
          import('@features/data-sources/data-sources.module')
            .then((mod) => mod.DataSourcesModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        data: {
          permissions: ['view_datasource'],
        },
      },
      {
        path: 'user-management',
        loadChildren: () =>
          import('@features/user-management')
            .then((mod) => mod.UserManagementModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_user'],
        },
      },
      {
        path: 'role-management',
        loadChildren: () =>
          import('@features/role-management')
            .then((mod) => mod.RoleManagementModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_role'],
        },
      },
      {
        path: 'categories',
        loadChildren: () =>
          import('@features/categories')
            .then((mod) => mod.CategoriesModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_category'],
        },
      },
      {
        path: 'pipelines',
        loadChildren: () =>
          import('@features/pipelines')
            .then((mod) => mod.PipelinesModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_pipeline'],
        },
      },
      {
        path: 'connectors',
        loadChildren: () =>
          import('@features/connectors')
            .then((mod) => mod.ConnectorsModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_connector'],
        },
      },
      {
        path: 'audit-log',
        loadChildren: () =>
          import('@features/audit-log')
            .then((mod) => mod.AuditLogModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_auditlog'],
        },
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('@features/notification-management')
            .then((mod) => mod.NotificationManagementModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_notifications'],
        },
      },
      {
        path: 'vpn-configuration',
        loadChildren: () =>
          import('@features/vpn-configuration')
            .then((mod) => mod.VpnConfigurationModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_vpnconfig'],
        },
      },
      {
        path: 'departments',
        loadChildren: () =>
          import('@features/departments')
            .then((mod) => mod.DepartmentsModule)
            .catch(() => {
              return ChunkLoadErrorHandlerModule;
            }),
        canActivate: [accessPageGuard],
        data: {
          permissions: ['view_department'],
        },
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class OrganizationsRoutingModule {}
